// @ts-nocheck
import { Stack, Title, Text, SimpleGrid, Code } from '@mantine/core';
import { IconUsers, IconCurrencyDollar, IconChartLine, IconActivity } from '@tabler/icons-react';
import { Card } from '../../../../components/ui';
import {
  KpiCard,
  KpiCardWithTrend,
  KpiCardWithSparkline,
  KpiCardMinimal,
  KpiCardGradient,
} from '../../../../components/advanced/Widgets';
import { mockKpiMetrics, mockKpiWithSparklines } from '../../mockData/widgets';

export default function KpiWidgetsPage() {
  return (
    <Stack gap="xl">
      {/* Header */}
      <Stack gap="xs">
        <Title order={2}>KPI / Stat Card Widgets</Title>
        <Text c="dimmed" size="sm">
          Display key performance indicators with multiple style variants including trends,
          sparklines, and animations. Perfect for dashboards and executive summaries.
        </Text>
      </Stack>

      {/* Basic KPI Card */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>KpiCard - Basic</Title>
          <Text size="sm" c="dimmed">
            Simple KPI display with icon, title, and value. Supports animated counting and
            multiple sizes.
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <KpiCard
              title="Total Users"
              value={mockKpiMetrics[0].value}
              icon={<IconUsers size={24} />}
              color="blue"
              animate
            />
            <KpiCard
              title="Revenue"
              value={`$${mockKpiMetrics[3].value.toLocaleString()}`}
              icon={<IconCurrencyDollar size={24} />}
              color="green"
              size="md"
            />
            <KpiCard
              title="Active Sessions"
              value={mockKpiMetrics[13].value}
              icon={<IconActivity size={24} />}
              color="violet"
              variant="outlined"
            />
            <KpiCard
              title="API Requests"
              value={mockKpiMetrics[8].value.toLocaleString()}
              icon={<IconChartLine size={24} />}
              color="orange"
              variant="gradient"
              gradientVariant="warning"
            />
          </SimpleGrid>

          <Code block>
{`<KpiCard
  title="Total Users"
  value={12453}
  icon={<IconUsers size={24} />}
  color="blue"
  animate
/>`}
          </Code>
        </Stack>
      </Card>

      {/* KPI Card with Trend */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>KpiCardWithTrend</Title>
          <Text size="sm" c="dimmed">
            KPI card with trend indicators showing percentage change and direction. Automatically
            color-codes trends as positive or negative.
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            {mockKpiMetrics.slice(0, 4).map((kpi, idx) => (
              <KpiCardWithTrend
                key={idx}
                title={kpi.title}
                value={typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                icon={<IconChartLine size={24} />}
                trend={kpi.trend}
                color={['blue', 'green', 'violet', 'orange'][idx]}
              />
            ))}
          </SimpleGrid>

          <Code block>
{`<KpiCardWithTrend
  title="Monthly Revenue"
  value="$45,231"
  trend={{ value: 15.7, direction: 'up', isPositive: true }}
  icon={<IconCurrencyDollar />}
/>`}
          </Code>
        </Stack>
      </Card>

      {/* KPI Card with Sparkline */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>KpiCardWithSparkline</Title>
          <Text size="sm" c="dimmed">
            KPI card with mini line chart showing 30-day trend. Perfect for visualizing
            historical performance at a glance.
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {mockKpiWithSparklines.slice(0, 6).map((kpi, idx) => (
              <KpiCardWithSparkline
                key={idx}
                title={kpi.title}
                value={typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                sparklineData={kpi.sparklineData}
                description="Last 30 days"
                color={['blue', 'green', 'violet', 'orange', 'teal', 'cyan'][idx]}
              />
            ))}
          </SimpleGrid>

          <Code block>
{`<KpiCardWithSparkline
  title="Active Users"
  value={8234}
  sparklineData={[50, 55, 60, 58, 65, 70, ...]}
  description="Last 30 days"
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Minimal KPI Card */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>KpiCardMinimal</Title>
          <Text size="sm" c="dimmed">
            Ultra-compact KPI display without borders or backgrounds. Perfect for dense
            dashboards or when space is limited.
          </Text>

          <SimpleGrid cols={{ base: 2, sm: 4, lg: 6 }} spacing="md">
            {mockKpiMetrics.slice(0, 6).map((kpi, idx) => (
              <KpiCardMinimal
                key={idx}
                title={kpi.title}
                value={typeof kpi.value === 'number' ? kpi.value.toLocaleString() : kpi.value}
                color={['blue', 'green', 'violet', 'orange', 'teal', 'cyan'][idx]}
              />
            ))}
          </SimpleGrid>

          <Code block>
{`<KpiCardMinimal
  title="Pending Tasks"
  value={87}
  color="orange"
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Gradient KPI Card */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>KpiCardGradient</Title>
          <Text size="sm" c="dimmed">
            Eye-catching KPI cards with gradient backgrounds. Uses the theme's gradient system
            for consistent, beautiful displays.
          </Text>

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
            <KpiCardGradient
              title="Total Users"
              value={mockKpiMetrics[0].value.toLocaleString()}
              trend={mockKpiMetrics[0].trend}
              icon={<IconUsers size={24} />}
              gradientVariant="primary"
            />
            <KpiCardGradient
              title="Revenue"
              value={`$${mockKpiMetrics[3].value.toLocaleString()}`}
              trend={mockKpiMetrics[3].trend}
              icon={<IconCurrencyDollar size={24} />}
              gradientVariant="success"
            />
            <KpiCardGradient
              title="Active Users"
              value={mockKpiMetrics[1].value.toLocaleString()}
              trend={mockKpiMetrics[1].trend}
              icon={<IconActivity size={24} />}
              gradientVariant="info"
            />
            <KpiCardGradient
              title="Conversion Rate"
              value={`${mockKpiMetrics[5].value}%`}
              trend={mockKpiMetrics[5].trend}
              icon={<IconChartLine size={24} />}
              gradientVariant="warning"
            />
          </SimpleGrid>

          <Code block>
{`<KpiCardGradient
  title="Total Users"
  value="12,453"
  trend={{ value: 12.5, direction: 'up' }}
  gradientVariant="primary"
/>`}
          </Code>
        </Stack>
      </Card>

      {/* Use Cases */}
      <Card withBorder p="lg">
        <Stack gap="md">
          <Title order={4}>Common Use Cases</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            <Stack gap="xs">
              <Text size="sm" fw={500}>Executive Dashboards</Text>
              <Text size="xs" c="dimmed">
                Display high-level metrics with trends for quick executive insights.
                Use gradient variants for visual impact.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Real-time Monitoring</Text>
              <Text size="xs" c="dimmed">
                Show live system metrics with animated counting. Use minimal variant
                for dense monitoring displays.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Performance Tracking</Text>
              <Text size="xs" c="dimmed">
                Track KPIs over time with sparklines. Perfect for identifying trends
                and patterns at a glance.
              </Text>
            </Stack>
            <Stack gap="xs">
              <Text size="sm" fw={500}>Comparison Views</Text>
              <Text size="xs" c="dimmed">
                Compare metrics side-by-side with trend indicators. Use color coding
                to highlight performance differences.
              </Text>
            </Stack>
          </SimpleGrid>
        </Stack>
      </Card>
    </Stack>
  );
}
